import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion, ActionIcon, Badge, Button, Group, NumberInput, Stack, Text, TextInput, UnstyledButton,
} from '@mantine/core';
import { useAppContext } from '../../contexts/AppContext';
import TranscriptReview from '../TranscriptReview';
import GereedheidPaneel from '../GereedheidPaneel';
import { InfoGrid, PaneelKop, netGetal } from './bouwstenen';
import { akkoordKleur } from '../LegendaPaneel';

// Zelfde onderscheid als de kaart: alleen uitvoeringsprojecten van het type
// station staan als stip op de kaart; tracés e.d. zijn wel project, geen station.
const isStation = (p) =>
  p.soort_project === 'UITVOERING' && (p.type || '').toLowerCase().includes('station');

const SESSIE_STATUS = {
  'actief': { kleur: 'red', label: 'Actief' },
  'te-reviewen': { kleur: 'yellow', label: 'Te reviewen' },
  'afgerond': { kleur: 'green', label: 'Afgerond' },
};

export default function BuurtSidebar() {
  const {
    selectedBuurt, setSelectedBuurt, addToast, fetchWithAuth,
    daalAfNaarProject, user,
  } = useAppContext();
  const [klicUuid, setKlicUuid] = useState('');
  const [bezig, setBezig] = useState(false);
  const [sessies, setSessies] = useState([]);
  const [reviewSessie, setReviewSessie] = useState(null);
  const [projecten, setProjecten] = useState([]);
  // Beheer: netstrategie-jaartal + start uitvoering (alleen admin)
  const isAdmin = !!user?.admin;
  const [netstrat, setNetstrat] = useState('');
  const [startDatum, setStartDatum] = useState('');

  const buurtcode = selectedBuurt?.buurtcode;
  const laadSessies = useCallback(async () => {
    if (!buurtcode) return;
    try {
      const res = await fetchWithAuth(`/api/sessies?buurtcode=${encodeURIComponent(buurtcode)}`);
      const data = await res.json();
      if (data.success) setSessies(data.sessies);
    } catch (e) {
      console.warn('Sessies laden mislukt:', e);
    }
  }, [buurtcode, fetchWithAuth]);

  useEffect(() => { laadSessies(); }, [laadSessies]);

  const verwijderSessie = async (s) => {
    if (!window.confirm(
      `Belangentafel ${s.belangentafel_nr} definitief verwijderen? `
      + 'De opname, het transcript en alle gegevens gaan verloren.')) return;
    try {
      const res = await fetchWithAuth(`/api/sessies/${s.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { addToast && addToast('Belangentafel verwijderd', 'success'); laadSessies(); }
      else addToast && addToast(data.error || 'Verwijderen mislukt', 'error');
    } catch (e) {
      addToast && addToast('Verwijderen mislukt', 'error');
    }
  };

  const [briefBezig, setBriefBezig] = useState(false);
  const download = async (pad, bestandsnaam, bezigSetter) => {
    if (bezigSetter) bezigSetter(true);
    try {
      const res = await fetchWithAuth(pad);
      if (!res.ok) { addToast && addToast('Downloaden mislukt', 'error'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = bestandsnaam;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      addToast && addToast('Downloaden mislukt', 'error');
    } finally {
      if (bezigSetter) bezigSetter(false);
    }
  };

  useEffect(() => {
    if (!buurtcode) return;
    let actueel = true;
    fetchWithAuth(`/api/projecten?buurtcode=${encodeURIComponent(buurtcode)}`)
      .then(res => res.json())
      .then(data => { if (actueel && data.features) setProjecten(data.features); })
      .catch(e => console.warn('Projecten laden mislukt:', e));
    return () => { actueel = false; };
  }, [buurtcode, fetchWithAuth]);

  // Bewerkvelden vullen vanuit de geselecteerde buurt
  useEffect(() => {
    const jaar = selectedBuurt?.netstrategie_jaartal;
    setNetstrat(jaar != null && jaar !== '' ? parseInt(jaar, 10) : '');
    setStartDatum(
      selectedBuurt?.plan_start_uitvoering
        ? String(selectedBuurt.plan_start_uitvoering).slice(0, 10)
        : '',
    );
  }, [buurtcode, selectedBuurt]);

  // Opslaan bij blur; alleen als er echt iets veranderd is (geen audit-spam).
  const slaBuurtOp = async () => {
    const jaarNu = netstrat === '' || netstrat === null ? null : Number(netstrat);
    const startNu = startDatum || null;
    const jaarWas = selectedBuurt?.netstrategie_jaartal != null && selectedBuurt.netstrategie_jaartal !== ''
      ? Number(selectedBuurt.netstrategie_jaartal) : null;
    const startWas = selectedBuurt?.plan_start_uitvoering
      ? String(selectedBuurt.plan_start_uitvoering).slice(0, 10) : null;
    if (jaarNu === jaarWas && startNu === startWas) return;
    try {
      const res = await fetchWithAuth(`/api/buurten/${encodeURIComponent(buurtcode)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ netstrategie_jaartal: jaarNu, plan_start_uitvoering: startNu }),
      });
      const data = await res.json();
      if (data.success) {
        setSelectedBuurt(prev => ({
          ...prev, netstrategie_jaartal: jaarNu, plan_start_uitvoering: startNu,
        }));
      } else {
        addToast(data.error || 'Opslaan mislukt', 'error');
      }
    } catch (e) {
      addToast('Opslaan mislukt', 'error');
    }
  };

  // Zelfde gedrag als een zoekresultaat: naar het punt vliegen en selecteren
  // Volledige afdaling naar projectniveau (kruimelpad + juiste kaartlagen +
  // energiehuisje), i.p.v. alleen inzoomen en het paneel tonen.
  const openProject = (feature) => daalAfNaarProject(feature);

  if (!selectedBuurt) return null;

  const haalKlic = async () => {
    if (!klicUuid.trim()) return;
    setBezig(true);
    try {
      const resp = await fetch(`/api/klic/${selectedBuurt.buurtcode}/${klicUuid.trim()}`, { method: 'POST' });
      if (resp.ok) addToast('KLIC-data wordt opgehaald', 'success');
      else addToast('Fout bij KLIC-aanvraag', 'error');
    } catch (e) {
      addToast('Fout bij KLIC-aanvraag', 'error');
    } finally {
      setBezig(false);
    }
  };

  const b = selectedBuurt;
  // Alleen stations tonen (tracés e.d. zijn wel project, maar geen station).
  const stations = projecten.filter(f => isStation(f.properties));
  const startUitvoering = b.plan_start_uitvoering
    ? new Date(b.plan_start_uitvoering).toLocaleDateString('nl-NL', { year: 'numeric', month: 'long' })
    : null;

  return (
    <>
      <PaneelKop
        label="Buurt"
        titel={b.buurtnaam}
        subtitel={b.gemeentenaam || ''}
        onSluit={() => setSelectedBuurt(null)}
        offsetTop={44}
      />
      <Stack gap="md" p="md">
        <InfoGrid items={[
          { label: 'Huidige MSR’s', waarde: netGetal(b.huidig_aantal_msr_s) },
          { label: 'BAR gereed', waarde: b.bar_gereed },
          {
            // Beheerder bewerkt de netstrategie (jaar) inline in het bestaande veld.
            label: 'Netstrategie',
            gecentreerd: true,
            waarde: isAdmin ? (
              <NumberInput
                size="xs" variant="unstyled" hideControls allowDecimal={false}
                min={2000} max={2100} placeholder="jaar"
                value={netstrat} onChange={setNetstrat} onBlur={slaBuurtOp}
                styles={{ input: { fontWeight: 600, padding: 0, minHeight: 0, height: 22, textAlign: 'center' } }}
              />
            ) : netGetal(b.netstrategie_jaartal),
          },
          {
            label: 'Start uitvoering',
            gecentreerd: true,
            waarde: isAdmin ? (
              <TextInput
                size="xs" type="date" variant="unstyled"
                value={startDatum} onChange={e => setStartDatum(e.currentTarget.value)} onBlur={slaBuurtOp}
                styles={{ input: { fontWeight: 600, padding: 0, minHeight: 0, height: 22, textAlign: 'center' } }}
              />
            ) : startUitvoering,
          },
        ]} />


        <Accordion variant="contained" chevronPosition="left">
          {stations.length > 0 && (
            <Accordion.Item value="stations">
              <Accordion.Control>
                <Text size="sm">Stations ({stations.length})</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap={2}>
                  {[...stations]
                    .sort((a, b) =>
                      (a.properties.projectnummer || '').localeCompare(b.properties.projectnummer || ''))
                    .map(f => {
                      const p = f.properties;
                      return (
                        <UnstyledButton
                          key={p.projectnummer}
                          onClick={() => openProject(f)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '4px 6px', borderRadius: 6, width: '100%',
                          }}
                          className="station-rij"
                        >
                          <span style={{
                            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                            background: akkoordKleur(p.akkoord_status), border: '1px solid #ddd',
                          }} />
                          <Text size="sm" fw={500} style={{ flexShrink: 0 }}>{p.projectnummer}</Text>
                          <Text size="xs" c="dimmed" truncate style={{ flex: 1, textAlign: 'left' }}>
                            {(p.type || '').trim() || (p.soort_project === 'HOOFD' ? 'Hoofdproject buurtaanpak' : p.soort_project)}
                          </Text>
                        </UnstyledButton>
                      );
                    })}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}

          <Accordion.Item value="belangentafels">
            <Accordion.Control>
              <Text size="sm">Belangentafels ({sessies.length})</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={4}>
                {sessies.length === 0 && (
                  <Text size="xs" c="dimmed">Nog geen belangentafels gehouden.</Text>
                )}
                {sessies.map(s => {
                  const status = SESSIE_STATUS[s.status] || { kleur: 'gray', label: s.status };
                  return (
                    <Group key={s.id} justify="space-between" wrap="nowrap"
                      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', paddingBottom: 4 }}>
                      <Stack gap={0}>
                        <Text size="sm">Belangentafel {s.belangentafel_nr}</Text>
                        <Text size="xs" c="dimmed">
                          {s.gestart_op ? new Date(s.gestart_op).toLocaleDateString('nl-NL') : ''}
                        </Text>
                      </Stack>
                      <Group gap="xs" wrap="nowrap">
                        <Badge color={status.kleur} variant="light" size="sm">{status.label}</Badge>
                        {s.status === 'te-reviewen' && (
                          <Button size="compact-xs" onClick={() => setReviewSessie(s)}>Review</Button>
                        )}
                        {s.status === 'afgerond' && (
                          <Button size="compact-xs" variant="default" onClick={() => setReviewSessie(s)}>Bekijk</Button>
                        )}
                        <ActionIcon variant="subtle" color="red" size="sm"
                          aria-label={`Verwijder belangentafel ${s.belangentafel_nr}`}
                          onClick={() => verwijderSessie(s)}>
                          ✕
                        </ActionIcon>
                      </Group>
                    </Group>
                  );
                })}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="klic">
            <Accordion.Control>
              <Text size="sm">KLIC-data ophalen</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Group gap="xs" align="flex-end">
                <TextInput
                  label="KLIC-UUID"
                  placeholder="Voer UUID in..."
                  value={klicUuid}
                  onChange={e => setKlicUuid(e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <Button onClick={haalKlic} loading={bezig} disabled={!klicUuid.trim()}>Ophalen</Button>
              </Group>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="bewonersbrieven">
            <Accordion.Control>
              <Text size="sm">Bewonersbrieven</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={6}>
                <Text size="xs" c="dimmed">
                  Genereert de aankondigingsbrief (Word) voorgevuld met de gemeente,
                  periode, stations en omgevingsmanager, plus een KML met de plek van
                  de elektriciteitshuisjes (voor de bijlage). Controleer/vul de
                  gemarkeerde «…»-plekken zelf aan.
                </Text>
                <Group gap="xs">
                  <Button size="compact-xs" color="groen" loading={briefBezig}
                    onClick={() => download(
                      `/api/buurten/${encodeURIComponent(buurtcode)}/bewonersbrief`,
                      `bewonersbrief_${(b.buurtnaam || buurtcode).replace(/ /g, '_')}.docx`,
                      setBriefBezig)}>
                    Aankondigingsbrief (Word)
                  </Button>
                  <Button size="compact-xs" variant="default"
                    onClick={() => download(
                      `/api/buurten/${encodeURIComponent(buurtcode)}/placemarks.kml`,
                      `placemarks_${(b.buurtnaam || buurtcode).replace(/ /g, '_')}.kml`)}>
                    Placemarks (KML)
                  </Button>
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="rapportages">
            <Accordion.Control>
              <Text size="sm">Rapportages</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <GereedheidPaneel buurtcode={buurtcode} buurtnaam={b.buurtnaam} />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>

      <TranscriptReview
        sessie={reviewSessie ? { ...reviewSessie, buurtcode } : null}
        geopend={!!reviewSessie}
        onSluit={() => setReviewSessie(null)}
        onAfgerond={laadSessies}
      />
    </>
  );
}
